// frontend/src/components/auditor/AuditorCompliance.js
// Compliance items, annual certificates, and conflict checks for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'compliance', label: 'Compliance Items' },
  { id: 'certificates', label: 'Annual Certificates' },
  { id: 'conflicts', label: 'Conflict Checks' },
];

const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const PriorityBadge = ({ priority }) => {
  const colors = {
    'HIGH': 'bg-red-100 text-red-700',
    'MEDIUM': 'bg-amber-100 text-amber-700',
    'LOW': 'bg-green-100 text-green-700',
    'CRITICAL': 'bg-red-200 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[priority] || 'bg-gray-100 text-gray-600'}`}>
      {priority || '—'}
    </span>
  );
};

export default function AuditorCompliance({ authAxios }) {
  const [subTab, setSubTab] = useState('compliance');
  const [complianceItems, setComplianceItems] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [ci, cert, conf] = await Promise.all([
      fetchAllPages(authAxios, 'compliance-items/'),
      fetchAllPages(authAxios, 'annual-certificates/'),
      fetchAllPages(authAxios, 'conflict-checks/'),
    ]);
    setComplianceItems(ci);
    setCertificates(cert);
    setConflicts(conf);
    if (ci.length === 0 && cert.length === 0 && conf.length === 0) {
      setFetchError('No compliance data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Stats
  const overdueItems = complianceItems.filter(c =>
    c.status === 'Overdue' || c.status === 'OVERDUE' ||
    (c.due_date && new Date(c.due_date) < new Date() && c.status !== 'Completed' && c.status !== 'COMPLETED')
  ).length;
  const completedItems = complianceItems.filter(c => c.status === 'Completed' || c.status === 'COMPLETED').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Compliance</h2>
        <p className="text-sm text-gray-500 mt-1">Compliance items, annual certificates, and conflict-of-interest checks.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: complianceItems.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Completed', value: completedItems, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Overdue', value: overdueItems, color: 'text-red-700 bg-red-50 border-red-200' },
          { label: 'Conflict Checks', value: conflicts.length, color: 'text-purple-700 bg-purple-50 border-purple-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'compliance' && (
        <AuditorTable
          title={`Compliance Items (${complianceItems.length})`}
          data={complianceItems}
          columns={[
            { key: 'title', label: 'Title', render: (v, row) => row.title || row.name || '—' },
            { key: 'category', label: 'Category' },
            { key: 'status', label: 'Status', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'Completed' || v === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                v === 'Overdue' || v === 'OVERDUE' ? 'bg-red-100 text-red-700' :
                v === 'Pending' || v === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                v === 'In Progress' || v === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'priority', label: 'Priority', render: (v) => <PriorityBadge priority={v} /> },
            { key: 'due_date', label: 'Due Date', render: (v) => fmtDate(v) },
            { key: 'assigned_to_name', label: 'Assigned To', render: (v, row) =>
              row.assigned_to_name || row.assigned_to_details?.full_name || '—'
            },
            { key: 'completed_date', label: 'Completed', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'compliance_items.csv' }}
          emptyMessage="No compliance items found."
        />
      )}

      {subTab === 'certificates' && (
        <AuditorTable
          title={`Annual Certificates (${certificates.length})`}
          data={certificates}
          columns={[
            { key: 'certificate_type', label: 'Type', render: (v, row) => row.certificate_type || row.type || '—' },
            { key: 'staff_name', label: 'Staff Member', render: (v, row) =>
              row.staff_name || row.staff_details?.full_name || '—'
            },
            { key: 'year', label: 'Year' },
            { key: 'status', label: 'Status', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'Valid' || v === 'VALID' || v === 'Active' ? 'bg-green-100 text-green-700' :
                v === 'Expired' || v === 'EXPIRED' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'issue_date', label: 'Issued', render: (v) => fmtDate(v) },
            { key: 'expiry_date', label: 'Expires', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'annual_certificates.csv' }}
          emptyMessage="No annual certificates found."
        />
      )}

      {subTab === 'conflicts' && (
        <AuditorTable
          title={`Conflict Checks (${conflicts.length})`}
          data={conflicts}
          columns={[
            { key: 'case_reference', label: 'Case', render: (v, row) => row.case_reference || row.case_details?.reference || '—' },
            { key: 'client_name', label: 'Client', render: (v, row) => row.client_name || row.client_details?.name || '—' },
            { key: 'result', label: 'Result', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'Clear' || v === 'CLEAR' || v === 'No Conflict' ? 'bg-green-100 text-green-700' :
                v === 'Conflict Found' || v === 'CONFLICT' ? 'bg-red-100 text-red-700' :
                v === 'Potential' || v === 'POTENTIAL' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'checked_by_name', label: 'Checked By', render: (v, row) =>
              row.checked_by_name || row.checked_by_details?.full_name || '—'
            },
            { key: 'date_checked', label: 'Date Checked', render: (v) => fmtDate(v) },
            { key: 'notes', label: 'Notes' },
          ]}
          exportCsv={{ filename: 'conflict_checks.csv' }}
          emptyMessage="No conflict checks found."
        />
      )}
    </div>
  );
}
